import React from "react"
import { useLocation } from "@reach/router"
import get from "lodash/get"
import { urlHelper } from "../util"
import Link from "./LinkWrapper"

export interface CalloutProps {
  /**
   * The data from Kontent that provides the text and link
   * */
  contentItem: {
    elements: {
      prepend_value: {
        value: string
      }
      value: {
        value: string
      }
      append_value: {
        value: string
      }
      label: {
        value: string
      }
      localized_link_label: {
        value: [
          {
            system: {
              type: string
            }
            elements: {
              localized_string_value: {
                value: string
              }
            }
          }
        ]
      }
      custom_link_label: {
        value: string
      }
      link_to: {
        value: {
          resolved_url: string
          system: {
            codename: string
          }
        }[]
      }
    }
  }
  /**
   * The color of the text for _text_ style callouts
   * */
  colorScheme?: "light" | "dark"
  /**
   * An optional class to apply to the wrapper element
   * */
  className?: string
  /**
   * The current language. Used to make text modifications based on language.
   */
  language?: "en" | "de" | "es" | "fr" | "it" | "pl" | "pt" | "jp" | "cn"
  /**
   * Display the text in a callout in rows (vertical stack) or columns
   * (horizontal stack)
   * */
  direction?: "row" | "column"
  /**
   * The alignment of the text in each callout
   */
  textAlign?: "left" | "center"
  /**
   * The size of the text in the callout
   * */
  textSize?: "xs" | "sm" | "base"
  /**
   * The index of the callout in a list of callouts (used to add spacing)
   * */
  index?: number
  /**
   * The style of the callout
   * */
  style?: "text" | "base" | "card"
}

const Callout = ({
  contentItem,
  colorScheme,
  className = "",
  language = "en",
  direction,
  style,
  textAlign,
  textSize,
  index,
}: CalloutProps) => {
  const location = useLocation()
  const { pathname } = location
  const prependValue = get(contentItem, "elements.prepend_value.value")
  const value = get(contentItem, "elements.value.value")
  const appendValue = get(contentItem, "elements.append_value.value")
  const label = get(contentItem, "elements.label.value")
  const localizedLinkLabel = get(
    contentItem,
    "elements.localized_link_label.value[0].elements.localized_string_value.value"
  )
  const customLinkLabel = get(contentItem, "elements.custom_link_label.value")
  const linkTo = get(contentItem, "elements.link_to.value[0]")
  const linkIsCurrent = linkTo?.resolved_url === pathname
  const linkLabel = customLinkLabel
    ? customLinkLabel
    : localizedLinkLabel
    ? localizedLinkLabel
    : null

  const needsHyphens = language === "de" || language === "pl"

  let valueClassName, labelClassName

  switch (style) {
    case "text":
      switch (textSize) {
        case "xs":
          valueClassName = "text-2xl"
          labelClassName = "text-xs"
          break
        case "sm":
          valueClassName = "text-3xl lg:text-4xl"
          labelClassName = "text-sm"
          break
        case "base":
        default:
          valueClassName = "text-4xl lg:text-5xl"
          labelClassName = "text-base"
      }

      if (direction === "row") {
        return (
          <div className={className ? className : undefined}>
            <div
              className={`flex flex-wrap gap-x-1 ${
                textAlign === "center" ? "justify-center" : ""
              }`}
            >
              {/* ${colorScheme === "dark" ? `text-orange` : `text-cyan`} */}
              {prependValue ? (
                <span
                  className={`font-bold block uppercase tracking-wide text-base/none text-gray-900 w-full ${
                    textAlign === "center" ? "text-center" : ""
                  }`}
                >
                  {prependValue}
                </span>
              ) : null}
              <span
                className={`text-cyan ${valueClassName} block font-bold leading-none shrink-0`}
              >
                {value}
              </span>
              {appendValue ? (
                <span
                  className={`font-bold inline-block uppercase tracking-wide text-base/none text-gray-900 self-end leading-none pb-0.5`}
                >
                  {appendValue}
                </span>
              ) : null}
            </div>
            <span
              className={`${needsHyphens ? "hyphens-auto" : ""} ${
                textAlign === "center" ? "text-center" : ""
              } ${labelClassName} font-bold leading-tight uppercase ${
                colorScheme === "dark" ? `text-gray-900` : `text-gray-700`
              } block`}
            >
              {label}
            </span>
            {linkTo && linkLabel && !linkIsCurrent ? (
              <span className={`${labelClassName} mt-1 font-normal block`}>
                <Link
                  to={urlHelper(linkTo)}
                  data-analytics-title={get(
                    contentItem,
                    "elements.link_to.value[0].system.name"
                  )}
                  className={`text-action-link no-underline hover:underline`}
                >
                  {linkLabel}
                  <i className="icon-chevron-right"></i>
                </Link>
              </span>
            ) : null}
          </div>
        )
      } else {
        return (
          <>
            {/* ${colorScheme === "dark" ? `text-orange` : `text-cyan`} */}
            <th
              className={`text-cyan text-right ${valueClassName} font-bold leading-none ${
                index > 0 ? `pt-4` : ``
              }`}
            >
              {prependValue ? (
                <span className="block uppercase tracking-wide text-base text-gray-900">
                  {prependValue}
                </span>
              ) : null}
              {value}
              {appendValue ? (
                <span className="block uppercase tracking-wide text-base text-gray-900">
                  {appendValue}
                </span>
              ) : null}
            </th>
            <td
              className={`pl-4 align-middle ${labelClassName} font-bold leading-tight ${
                colorScheme === "dark" ? `text-gray-900` : `text-gray-700`
              } ${index > 0 ? `pt-4` : ``}`}
            >
              {label}
              {linkTo && linkLabel && !linkIsCurrent ? (
                <p className={`text-xs mt-1 font-normal`}>
                  <Link
                    to={urlHelper(linkTo)}
                    data-analytics-title={get(
                      contentItem,
                      "elements.link_to.value[0].system.name"
                    )}
                    className={`text-action-link no-underline hover:underline`}
                  >
                    {linkLabel}
                    <i className="icon-chevron-right"></i>
                  </Link>
                </p>
              ) : null}
            </td>
          </>
        )
      }
    case "card":
    default:
      if (direction === "column") {
        switch (textSize) {
          case "xs":
            valueClassName = "text-2xl py-2 px-4"
            labelClassName = "text-xs py-2 px-4"
            break
          case "sm":
            valueClassName = "text-3xl lg:text-4xl p-4"
            labelClassName = "text-xs p-4"
            break
          case "base":
          default:
            valueClassName = "text-4xl lg:text-5xl p-4"
            labelClassName = "text-base p-4"
        }
        return (
          <div
            data-analytics-region="Call Out"
            className={`flex max-w-sm ${className}`}
          >
            {/*
             * If we decide to apply colorScheme to call outs
             * ${colorScheme === `dark` ? `bg-orange text-gray-800` : `bg-cyan text-white`}
             *
             */}
            <div
              className={`bg-cyan text-white flex shrink-0 items-center text-right ${valueClassName} font-bold leading-none rounded-l-lg ${
                index > 0 ? `mt-4` : ``
              }`}
            >
              <div>
                {prependValue && (
                  <span className="block uppercase tracking-wide text-xs">
                    {prependValue}
                  </span>
                )}
                {value}
                {appendValue && (
                  <span className="block uppercase tracking-wide text-xs">
                    {appendValue}
                  </span>
                )}
              </div>
            </div>
            <div
              className={`border-white border-l-4 flex items-center bg-gray-800 text-white  ${labelClassName} uppercase tracking-wider font-bold leading-tight rounded-r-lg`}
            >
              <div>
                <span
                  className={`${needsHyphens ? "hyphens-auto" : ""} uppercase`}
                >
                  {label}
                </span>
                {linkTo && linkLabel && !linkIsCurrent ? (
                  <p className={`text-xs mt-1 font-normal`}>
                    <Link
                      to={urlHelper(linkTo)}
                      data-analytics-title={get(
                        contentItem,
                        "elements.link_to.value[0].system.name"
                      )}
                      className={`text-white hover:text-gray-200 no-underline hover:underline transition-colors duration-150`}
                    >
                      {linkLabel}
                      <i className="icon-chevron-right"></i>
                    </Link>
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        )
      }
      return (
        <div
          data-analytics-region="Call Out"
          className={`flex flex-col grow bg-cyan text-white leading-none uppercase font-bold w-full ${className}`}
        >
          <div className={`w-full bg-cyan text-white py-4 px-6 pt-6 pb-2`}>
            <div className={`w-full`}>{prependValue}</div>
            <div
              className={`w-full uppercase font-bold text-6xl ${
                prependValue ? `` : `mt-4`
              }`}
            >
              {value}
              {appendValue ? (
                <span className={`inline-block text-2xl ml-1`}>
                  {appendValue}
                </span>
              ) : null}
            </div>
          </div>
          <div
            className={`w-full bg-gray-800 py-4 px-6 pb-6 border-white border-solid border-0 border-t-4 grow`}
          >
            <div className={`w-full bg-gray-800 leading-snug tracking-wider`}>
              {label}
            </div>
            {linkTo && linkLabel && !linkIsCurrent ? (
              <div className={`mt-2`}>
                <Link
                  to={urlHelper(linkTo)}
                  className={`normal-case font-normal text-sm text-white hover:text-gray-200 no-underline hover:underline`}
                  data-analytics-title={get(
                    contentItem,
                    "elements.link_to.value[0].system.name"
                  )}
                >
                  {linkLabel}
                  <i className="icon-chevron-right ml-1"></i>
                </Link>
              </div>
            ) : null}
          </div>
        </div>
      )
  }
}

export default Callout
